<template>
  <div class="summary-section" :data-aos="isInternalView ? '' : 'fade-up'" data-aos-once="true">
    <v-container>
      <v-expansion-panels v-model="panel" dark accordion multiple>
        <v-expansion-panel expand>
          <v-expansion-panel-header>
            <v-app-bar dark color="#212121">
              <v-toolbar-title style="text-align:center;">Reel Analysis Summary</v-toolbar-title>
              <v-spacer></v-spacer>
              
              <v-menu
                :close-on-content-click="false"
                bottom
                left
                v-if="showRequiredActions()"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="openColSettingsModal"
                  >
                    <i class="fas fa-cog fa-lg" data-toggle="tooltip" data-placement="top" title="Settings"></i>
                  </v-btn>
                </template>
              </v-menu>
              <v-btn
                v-if="showRequiredActions()"
                icon
                color="primary"
                @click="downloadSummary"
              >
                <i class="fas fa-download fa-lg" data-toggle="tooltip" data-placement="top" title="Download"></i>
              </v-btn>
              <v-btn
                v-if="isInternalView && showRequiredActions()"
                icon
                color="primary"
                @click.stop="$emit('open-analysis')"
              >
                <i class="fas fa-magic fa-lg" data-toggle="tooltip" data-placement="top" title="AI Analysis"></i>
              </v-btn>
            </v-app-bar>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card id="reel-analysis-summary" class="mx-auto" outlined color="#000000">
              <v-card-text>
                <div>
                  <!-- Group Filter Buttons (right aligned, no "All" button) -->
                  <div v-if="groups.length > 0" class="group-filter-btns-right">
                    <div class="group-btns">
                      <v-btn
                        v-for="group in groups"
                        :key="group.groupID"
                        :color="selectedGroup === group.groupID ? 'orange' : '#fff'"
                        :class="['group-btn', { 'active-btn': selectedGroup === group.groupID }]"
                        @click="selectedGroup = selectedGroup === group.groupID ? null : group.groupID"
                        depressed
                        small
                        style="margin-right:8px; color: #212121;"
                      >
                        {{ group.groupName || group.groupID }}
                      </v-btn>
                    </div>
                  </div>
                  <div class="text-right mb-2">
                    <!-- Save and Cancel buttons for drag-and-drop reordering -->
                    <v-btn
                      @click.native.stop
                      @click="cancelPostOrder"
                      color="warning"
                      dark
                      v-if="editPostOrder"
                      class="mr-2"
                      small
                    >
                      Cancel
                    </v-btn>

                    <v-btn
                      @click.native.stop
                      @click="savePostOrder"
                      color="success"
                      dark
                      v-if="editPostOrder"
                      class="mr-2"
                      small
                    >
                      <v-icon small>mdi-content-save</v-icon> Save Changes
                    </v-btn>
                  </div>
                  <v-data-table
                    dark
                    :headers="computedHeaders"
                    :items="filteredPosts"
                    item-key="key"
                    :items-per-page="-1"
                    class="elevation-15 report-summary-table"
                    :key="dataTableKey"
                    v-sortable-data-table
                    @sorted="handleDataTableDragDrop"
                    :custom-sort="customSort"
                  >
                    <template v-for="(h, index) in computedHeaders" v-slot:[`header.${h.value}`]="{ header }">
                      <legend-icon :key="index" :header="h" />
                    </template>
                    <template v-slot:item.name="{ item }">
                      <div class="d-flex align-center">
                        <v-icon
                          small
                          class="move-cursor"
                        >
                          mdi-arrow-all
                        </v-icon>
                        <v-btn
                          v-if="isInternalView"
                          class="btn-reload"
                          text
                          x-small
                          color="warning"
                          @click="refreshReportInf(item.id)"
                        >
                          <v-icon dark small>
                            mdi-reload
                          </v-icon>
                        </v-btn>
                        <a class="caption" style="color:#fff" :href="profileUrl(item)" target="_blank" rel="noopener noreferrer">@{{item.profileHandle || item.name}}</a>
                      </div>
                    </template>
                    <template v-slot:[`item.url`]="{ item }">
                      <td class="text-left">
                        <v-btn
                          x-small
                          text
                          color="secondary"
                          elevation="0"
                          :href="item.url"
                          :target="'_blank'"
                        >
                          View
                        </v-btn>
                      </td>
                    </template>
                    <template v-slot:item.outreach="{ item }">
                      {{item.outreach === 0 ? '-' : item.outreach | thousandSeprator}}
                    </template>
                    <template v-slot:item.likes="{ item }">
                      {{item.likes === 0 ? '-' : item.likes | thousandSeprator}}
                    </template>
                    <template v-slot:item.comments="{ item }">
                      {{item.comments === 0 ? '-' : item.comments | thousandSeprator}}
                    </template>
                    <template v-slot:item.saved="{ item }">
                      {{item.saved === 0 ? '-' : item.saved | thousandSeprator}}
                    </template>
                    <template v-slot:item.interactions="{ item }">
                      {{item.interactions === 0 ? '-' : item.interactions | thousandSeprator}}
                    </template>
                    <template v-slot:item.shares="{ item }">
                      {{item.shares === 0 ? '-' : item.shares | thousandSeprator}}
                    </template>
                    <template v-slot:item.views="{ item }">
                      {{item.views === 0 ? '-' : item.views}}
                    </template>
                    <template v-slot:item.reach="{ item }">
                      {{item.reach === 0 ? '-' : item.reach}}
                    </template>
                    <template v-slot:item.impressions="{ item }">
                      {{item.impressions === 0 ? '-' : item.impressions}}
                    </template>
                    <template v-slot:item.plays="{ item }">
                      {{item.plays === 0 ? '-' : showPlayTime(item.plays)}}
                    </template>
                    <template v-slot:item.watches="{ item }">
                      {{item.watches === 0 ? '-' : item.watches}}
                    </template>
                    <template v-slot:item.new_engagement_rate="{ item }">
                      <div>
                        {{item.new_engagement_rate === 0 ? '0' : item.new_engagement_rate}} %
                      </div>
                    </template>
                    <template v-slot:item.new_engagement_rate_view="{ item }">
                      <div>
                        {{item.new_engagement_rate_view === 0 ? '0' : item.new_engagement_rate_view}} %
                      </div>
                    </template>
                    <template v-slot:item.interaction_rate_plays="{ item }">
                      <div>
                        {{item.interaction_rate_plays === 0 ? '0' : item.interaction_rate_plays}} %
                      </div>
                    </template>
                    <template v-slot:item.engagement_rate_reach="{ item }">
                      <div>
                        {{item.engagement_rate_reach === 0 ? '0' : item.engagement_rate_reach}} %
                      </div>
                    </template>
                    <template v-slot:item.engagement_rate_sd="{ item }">
                      <div v-if="item.engagement_rate_sd && item.engagement_rate_sd !== '0.00'">
                        {{item.engagement_rate_sd === null ? '-' : item.engagement_rate_sd + '%'}}
                      </div>
                      <div v-else>
                        <v-icon small data-toggle="tooltip" data-placement="top" title="Hidden">mdi-eye-off</v-icon>
                      </div>
                    </template>
                    <template v-slot:item.median_engagement_rate="{ item }">
                      {{item.median_engagement_rate === null ? '-' : item.median_engagement_rate + '%'}}
                    </template>
                    <template v-slot:item.avg_likes="{ item }">
                      {{item.avg_likes === null ? '-' : item.avg_likes | thousandSeprator}}
                    </template>
                    <template v-slot:item.avg_comments="{ item }">
                      {{item.avg_comments === null ? '-' : item.avg_comments | thousandSeprator}}
                    </template>
                    <template v-slot:item.avg_views="{ item }">
                      {{item.avg_views === null ? '-' : item.avg_views | thousandSeprator}}
                    </template>
                    <template v-slot:item.avg_reels_plays="{ item }">
                      {{item.avg_reels_plays === null ? '-' : item.avg_reels_plays | thousandSeprator}}
                    </template>
                    <template slot="body.append">
                      <tr style="font-weight:500;">
                        <td><strong>TOTAL</strong></td>
                        <template v-for="header in computedHeaders">
                          <td :key="header.value" v-if="header.value !== 'name'">
                            <strong>
                              <template v-if="header.value === 'live_date'">-</template>
                              <template v-else-if="header.value === 'url'">-</template>
                              <template v-else-if="header.value === 'outreach'">{{ totalOutreach === 0 ? '-' : totalOutreach | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'reach'">{{ totalReach === 0 ? '-' : totalReach | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'impressions'">{{ totalImpressions === 0 ? '-' : totalImpressions | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'likes'">{{ totalLikes === 0 ? '-' : totalLikes | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'shares'">{{ totalShares === 0 ? '-' : totalShares | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'comments'">{{ totalComments === 0 ? '-' : totalComments | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'saved'">{{ totalSaved === 0 ? '-' : totalSaved | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'interactions'">{{ totalInteractions === 0 ? '-' : totalInteractions | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'views'">{{ totalViews == 0 ? '-' : totalViews | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'plays'">{{ showPlayTime(totalPlayTime) }}</template>
                              <template v-else-if="header.value === 'watches'">{{ totalWatches == 0 ? '-' : totalWatches | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'new_engagement_rate'">{{ totalEngagementRate }}%</template>
                              <template v-else-if="header.value === 'new_engagement_rate_view'">{{ totalEngagementRateView }}%</template>
                              <template v-else-if="header.value === 'interaction_rate_plays'">{{ totalInteractionRatePlays }}%</template>
                              <template v-else-if="header.value === 'engagement_rate_reach'">{{ totalEngagementRateReach }}%</template>
                              <template v-else-if="header.value === 'engagement_rate_sd'"> - </template>
                              <template v-else-if="header.value === 'median_engagement_rate'"> - </template>
                              <template v-else-if="header.value === 'avg_likes'"> - </template>
                              <template v-else-if="header.value === 'avg_comments'"> - </template>
                              <template v-else-if="header.value === 'avg_views'"> - </template>
                              <template v-else-if="header.value === 'avg_reels_plays'"> - </template>
                            </strong>
                          </td>
                        </template>
                      </tr>
                    </template>
                  </v-data-table>
                </div>
                <!-- <legend-section
                  :rightSections="postLegends"
                ></legend-section> -->
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
    <table-cols-show-hide-modal
      v-model="colSettingsDialog"
      title="Reel Analysis Summary"
      :items="tableColsItems"
      @updated="updatePostAnalysisStatus"
      :isSort="true"
    />
  </div>
</template>

<style scoped>
.report-summary-table ::v-deep .v-data-table__wrapper {
  background-color: #1f1f1f;
  border: 1px solid rgba(244, 244, 244, 0.16);
}

.report-summary-table ::v-deep table {
  background-color: #1f1f1f !important;
}

.report-summary-table ::v-deep table > thead > tr > th {
  background-color: #2a2a2a !important;
  border-bottom: 1px solid rgba(244, 244, 244, 0.18) !important;
  color: #f4f4f4 !important;
  font-weight: 700;
}

.report-summary-table ::v-deep table > thead > tr > th,
.report-summary-table ::v-deep table > tbody > tr > td {
  border-color: rgba(244, 244, 244, 0.14) !important;
}

.report-summary-table ::v-deep table > tbody > tr {
  background-color: #1f1f1f !important;
}

.report-summary-table ::v-deep table > tbody > tr:hover {
  background-color: #1f1f1f !important;
}

.report-summary-table ::v-deep table > tbody > tr > td,
.report-summary-table ::v-deep table > tbody > tr > td strong,
.report-summary-table ::v-deep table > tbody > tr > td .v-icon {
  color: #f4f4f4 !important;
}

.report-summary-table ::v-deep table > tbody > tr:last-child {
  background-color: #252525 !important;
}

.report-summary-table ::v-deep .v-data-footer {
  background-color: #1f1f1f;
  border-top: 1px solid rgba(244, 244, 244, 0.14);
  color: #f4f4f4 !important;
}

.report-summary-table ::v-deep .v-data-footer *,
.report-summary-table ::v-deep .v-data-footer .v-icon {
  color: #f4f4f4 !important;
}

.group-filter-btns-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
}
.group-btns {
  display: flex;
  align-items: center;
}
.group-btn {
  min-width: 48px;
  font-weight: 500;
  background: #fff !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  color: #212121 !important;
  transition: background 0.2s, color 0.2s;
}
.active-btn {
  background: orange !important;
  color: #fff !important;
}
</style>

<script>
import { getReportProfileUrl } from '@/utils/reportPlatform'
/* eslint-disable */
import html2canvas from 'html2canvas';
import LegendSection from './Legend'
import LegendIcon from './LegendIcon'
import campaignHelper from '../views/Campaign/campaignHelper';
import TableColsShowHideModal from '@/components/scoped/custom/TableColsShowHideModal'
import config from '@/config';
import campaignAPIs from '../views/Campaign/campaignAPIs';
export default {
  name: 'rpt_post_summary',
  props: ['posts', 'campaign', 'defaultCols', 'isInternalView'],
  components: {
    LegendSection,
    LegendIcon,
    TableColsShowHideModal
  },
  data () {
    return {
      page: 1,
      sortBy: 'live_date',
      sortDesc: true,
      dataTableKey: 0,
      disableSaveArrange: true,
      lineUpItems: [],
      panel: [0, 1],
      postLegends: [],
      selectedGroup: null,
      colSettingsDialog: false,
      tableColsItems: [],
      editPostOrder: false,
      originalPosts: [],
      showHideFields: [
        { name: 'liveDate', label: 'Live Date' },
        { name: 'url', label: 'Post URL' },
        { name: 'outreach', label: 'Audience Size' },
        { name: 'showReach', label: 'Accounts Reached' },
        { name: 'showImpressions', label: 'Impressions' },
        { name: 'likes', label: 'Likes' },
        { name: 'showShares', label: 'Shares' },
        { name: 'comments', label: 'Comments' },
        { name: 'showBookmarks', label: 'Bookmarks' },
        { name: 'showInteractions', label: 'Interactions' },
        { name: 'showViews', label: 'Plays' },
        { name: 'showPlays', label: 'Watch Time' },
        { name: 'showWatches', label: 'Average Watch Time' },
        { name: 'showEngagement', label: 'Engagement Rate by Audience Size' },
        { name: 'showEngagementView', label: 'Play Rate (by Audience Size)' },
        { name: 'showInteractionPlays', label: 'Engagement Rate by Plays' },
        { name: 'showEngRateReach', label: 'Engagement Rate by Reach' },
        { name: 'showAvgEngagement', label: 'Avg. Engagement Rate' },
        { name: 'showMediaEngagement', label: 'Median Engagement Rate' },
        { name: 'showAvgLikes', label: 'Average Likes' },
        { name: 'showAvgComments', label: 'Average Comments' },
        { name: 'showAvgViews', label: 'Average Views' },
        { name: 'showAvgReelsPlays', label: 'Average Reels Plays' }
      ],
      headers: [
        // ...existing headers...
        {
          text: 'Influencer',
          align: 'start',
          sortable: false,
          value: 'name'
        },
        {
          text: 'Live Date',
          value: 'live_date',
          legendText: 'Live Date'
        },
        {
          text: 'Post URL',
          value: 'url',
          legendText: 'Post URL'
        },
        {
          text: 'Audience Size',
          value: 'outreach',
          legendText: 'The number of followers the creator has at the time where the post was made '
        },
        {
          text: 'Accounts Reached',
          value: 'reach',
          legendText: 'The total number of unique users who viewed the content'
        },
        {
          text: 'Impressions',
          value: 'impressions',
          legendText: `The total number of times the content is displayed on an user's feed`
        },
        {
          text: 'Likes',
          value: 'likes',
          legendText: 'The number of times users have tapped the “like” button on a post.'
        },
        {
          text: 'Shares',
          value: 'shares',
          legendText: 'The number of times users have reposted / shared a post.'
        },
        {
          text: 'Comments',
          value: 'comments',
          legendText: 'The number of written responses to a post.'
        },
        {
          text: 'Bookmarks',
          value: 'saved',
          legendText: 'The number of times users have saved a post to their “Saved” folder.'
        },
        {
          text: 'Interactions',
          value: 'interactions',
          legendText: 'The sum of likes, comments, saves, shares'
        },
        {
          text: 'Plays',
          value: 'views',
          legendText: 'The number of times users have viewed a video for 3 seconds or more, not inclusive of looped views.'
        },
        {
          text: 'Watch Time',
          value: 'plays',
          legendText: `The all-time data for how many total hours, minutes, and seconds this piece of content was viewed.`
        },
        {
          text: 'Average Watch Time',
          value: 'watches',
          legendText: 'How many seconds your video is viewed for'
        },
        {
          text: 'Engagement Rate by Audience Size',
          value: 'new_engagement_rate',
          legendText: 'The total number of interactions (users who liked, commented, saved or shared the post) divided by audience size'
        },
        {
          text: 'Play Rate (by Audience Size)',
          value: 'new_engagement_rate_view',
          legendText: 'The total number of views divided by the audience size'
        },
        {
          text: 'Engagement Rate by Plays',
          value: 'interaction_rate_plays',
          legendText: 'The total number of interactions (users who liked, commented, saved or shared the post) divided by Plays'
        },
        {
          text: 'Engagement Rate by Reach',
          value: 'engagement_rate_reach',
          legendText: 'The total number of interactions (users who liked, commented, saved or shared the post) divided by reach'
        },
        {
          text: 'Avg. Engagement Rate',
          value: 'engagement_rate_sd',
          legendText: 'The average sum of engagement rate on the last 30 posts'
        },
        {
          text: 'Median Engagement Rate',
          value: 'median_engagement_rate',
          legendText: 'The middle engagement rate amongst the sorted engagement rate of all the creator’ within same tier comparison.'
        },
        {
          text: 'Average Likes',
          value: 'avg_likes',
          legendText: 'Average likes over the last 30 posts'
        },
        {
          text: 'Average Comments',
          value: 'avg_comments',
          legendText: 'Average comments over the last 30 posts'
        },
        {
          text: 'Average Views',
          value: 'avg_views',
          legendText: 'Average views over the last 30 posts'
        },
        {
          text: 'Average Reels Plays',
          value: 'avg_reels_plays',
          legendText: 'Average plays over the last 30 posts'
        }
      ],
      postRightSection: [
        // ...existing postRightSection...
        {
          title: 'Live Date',
          description: 'Live Date'
        },
        {
          title: 'Post URL',
          description: 'Post URL'
        },
        {
          title: 'Likes',
          description: 'Number of likes'
        },
        {
          title: 'Comments',
          description: 'Number of comments'
        },
        {
          title: 'Bookmarks',
          description: 'Number of bookmarks'
        },
        {
          title: 'Shares',
          description: 'Number of shares'
        },
        {
          title: 'Engagement Rate by Interactions',
          description: 'Followers divided by total interactions for this campaign'
        },
        {
          title: 'Views',
          description: 'Number of views'
        },
        {
          title: 'Reach',
          description: 'Number of unique user that seen your content'
        },
        {
          title: 'Impressions',
          description: 'Total number of times content have been shown to users'
        },
        {
          title: 'Total Play Time',
          description: 'The all-time data for how many total hours, minutes, and seconds this piece of content was viewed.'
        },
        {
          title: 'Average Watch Time',
          description: 'How many seconds your video is viewed for.'
        },
        {
          title: 'Average Engagement Rate',
          description: 'Average engagement for all advertorials and organic content'
        },
        {
          title: 'Median Engagement Rate',
          description: 'Engagement Rate within same tier comparison'
        },
        {
          title: 'Average Likes',
          description: 'Average likes over the last 30 posts'
        },
        {
          title: 'Average Comments',
          description: 'Average comments over the last 30 posts'
        },
        {
          title: 'Average Views',
          description: 'Average views over the last 30 posts'
        },
        {
          title: 'Average Reels Plays',
          description: 'Average plays over the last 30 posts'
        }
      ]
    }
  },
  computed: {
    sortedPosts() {
      return this.posts
    },
    groups() {
      const groupMap = new Map();
      this.sortedPosts.forEach(post => {
        if (post.groupID && post.groupName) {
          groupMap.set(post.groupID, post.groupName);
        }
        if (Array.isArray(post.reports)) {
          post.reports.forEach(report => {
            if (report.groupID && report.groupName) {
              groupMap.set(report.groupID, report.groupName);
            }
          });
        }
      });
      return Array.from(groupMap, ([groupID, groupName]) => ({
        groupID,
        groupName
      }));
    },
    filteredPosts() {
      if (!this.selectedGroup) return this.sortedPosts;
      return this.sortedPosts.filter(post => {
        if (post.groupID === this.selectedGroup) return true;
        if (Array.isArray(post.reports)) {
          return post.reports.some(report => report.groupID === this.selectedGroup);
        }
        return false;
      });
    },
    computedHeaders() {
      const status = this.campaign.reelAnalysisStatus || {};
      const finalHeaders = [];

      // 1. Add the "Influencer" column first (always fixed)
      const nameHeader = this.headers.find(h => h.value === 'name');
      if (nameHeader) {
        finalHeaders.push(nameHeader);
      }

      // 2. Iterate through the keys of reelAnalysisStatus to determine order and visibility
      for (const statusKey in status) {
        if (Object.prototype.hasOwnProperty.call(status, statusKey)) {
          const isVisible = status[statusKey];
          if (isVisible) {
            // Find the corresponding header from `headers` using `showHideFields` as a map
            const field = this.showHideFields.find(f => f.name === statusKey);
            if (field) {
              const header = this.headers.find(h => h.text === field.label);
              if (header) {
                finalHeaders.push(header);
              }
            } else {
              // Fallback for keys that might directly match header values
              const header = this.headers.find(h => h.value === statusKey);
              if (header) {
                finalHeaders.push(header);
              }
            }
          }
        }
      }
      return finalHeaders;
    },
    totalOutreach() {
      let outreach = 0;
      this.filteredPosts.map(item => {
        outreach += item.outreach ? Number(item.outreach) : 0
      })
      return outreach
    },
    totalEngagement() {
      let engagement = 0;
      this.filteredPosts.map(item => {
        engagement += item.engagement ? item.engagement : 0
      })
      return engagement
    },
    totalLikes() {
      let likes = 0;
      this.filteredPosts.map(item => {
        likes += item.likes ? item.likes : 0
      })
      return likes
    },
    totalComments() {
      let comments = 0;
      this.filteredPosts.map(item => {
        comments += item.comments ? item.comments : 0
      })
      return comments
    },
    totalSaved() {
      let saved = 0;
      this.filteredPosts.map(item => {
        saved += item.saved ? item.saved : 0
      })
      return saved
    },
    totalShares() {
      let shares = 0;
      this.filteredPosts.map(item => {
        shares += item.shares ? item.shares : 0
      })
      return shares
    },
    totalViews() {
      let views = 0;
      this.filteredPosts.map(item => {
        views += item.views ? item.views : 0
      })
      return views.toFixed(2)
    },
    totalInteractions() {
      let interactions = 0;
      this.filteredPosts.map(item => {
        interactions += item.interactions ? item.interactions : 0
      })
      return interactions
    },
    totalReach() {
      let reach = 0;
      this.filteredPosts.map(item => {
        reach += item.reach ? Number(item.reach) : 0
      })
      return reach
    },
    totalImpressions() {
      let impressions = 0;
      this.filteredPosts.map(item => {
        impressions += item.impressions ? item.impressions : 0
      })
      return impressions
    },
    totalPlayTime() {
      return campaignHelper.calculateTotalReelWatchTime(this.filteredPosts)
    },
    totalWatches() {
      let watches = 0;
      this.filteredPosts.map(item => {
        watches += item.watches ? Number(item.watches) : 0
      })
      return watches.toFixed(2)
    },
    totalEngagementRate() {
      let totalEng = 0;
      this.filteredPosts.map(item => {
        totalEng += item.new_engagement_rate ? item.new_engagement_rate : 0
      })
      return this.filteredPosts.length ? ((totalEng / this.filteredPosts.length)).toFixed(2) : 0
    },
    totalInteractionRatePlays() {
      let totalEng = 0;
      this.filteredPosts.map(item => {
        totalEng += item.interaction_rate_plays ? parseFloat(item.interaction_rate_plays) : 0
      })
      return this.filteredPosts.length ? ((totalEng / this.filteredPosts.length)).toFixed(2) : 0
    },
    totalEngagementRateReach() {
      let totalEng = 0;
      this.filteredPosts.map(item => {
        totalEng += item.engagement_rate_reach ? parseFloat(item.engagement_rate_reach) : 0
      })
      return this.filteredPosts.length ? ((totalEng / this.filteredPosts.length)).toFixed(2) : 0
    },
    totalEngagementRateView() {
      let totalEng = 0;
      this.filteredPosts.map(item => {
        totalEng += item.new_engagement_rate_view ? item.new_engagement_rate_view : 0
      })
      return this.filteredPosts.length ? ((totalEng / this.filteredPosts.length)).toFixed(2) : 0
    },
    currentUserRole: function() {
      return Number(this.$store.state.user.role);
    }
  },
  mounted() {
    this.updateLegend();
  },
  methods: {
    profileUrl(item) {
      return getReportProfileUrl(item, 'instagram')
    },
    customSort: function(items, index, isDesc) {
      items.sort((a, b) => {
        if (!(isNaN(a[index[0]]))) {
          if (!isDesc[0]) {
            return (a[index[0]] - b[index[0]]);
          } else {
            return (b[index[0]] - a[index[0]]);
          }
        } else {
          if (!isDesc[0]) {
            return (a[index[0]] < b[index[0]]) ? -1 : 1;
          } else {
            return (b[index[0]] < a[index[0]]) ? -1 : 1;
          }
        }
      })
      this.lineUpItems = Object.assign([], items)
      this.disableSaveArrange = false
      return items;
    },
    handleDataTableDragDrop(event, items) {
      // check and save drag n drop update for table items/ grouped items
      if (items.length === 0 || !this.showRequiredActions()) {
        return;
      }
      
      if (!this.editPostOrder) {
        this.originalPosts = JSON.parse(JSON.stringify(this.posts));
      }
      
      const itemsPerPage = 15;
      const oldIndex = ((this.page - 1) * itemsPerPage) + event.oldIndex;
      const newIndex = ((this.page - 1) * itemsPerPage) + event.newIndex;
      let tmpPosts = []
      let groupID = items[0].groupID
      if (groupID) {
        let group = this.sortedPosts.find(p => p.id === groupID)
        if (group) {
          tmpPosts = group.reports
        }
      } else {
        tmpPosts = Object.assign([], this.sortedPosts)
      }
      const movedItem = tmpPosts.splice(oldIndex, 1)[0]
      tmpPosts.splice(newIndex, 0, movedItem)
      if (groupID) {
        this.posts = this.sortedPosts.map(p => {
          if (p.id === groupID) {
            p.reports = tmpPosts.map(tmp => tmp)
          }
          return p
        })
      } else {
        this.posts = tmpPosts.map(tmp => tmp)
      }
      this.lineUpItems = []
      this.lineUpItems = Object.assign([], tmpPosts)
      this.disableSaveArrange = false
      this.editPostOrder = true
    },
    savePostOrder() {
      this.updateLineUp();
      this.editPostOrder = false;
      this.originalPosts = [];
    },
    cancelPostOrder() {
      this.posts = JSON.parse(JSON.stringify(this.originalPosts));
      this.lineUpItems = [];
      this.disableSaveArrange = true;
      this.editPostOrder = false;
      this.originalPosts = [];
    },
    async updateLineUp() {
      await this.lineUpItems.map((item, index) => {
        let data = {}
        if (item.reportGroup) {
          data.groupID = item.id
          data.lineUpIndex = index
          data.posts = item.reports.map((element, rindex) => {
            return Object.assign({}, { id: element.id, lineUpIndex: rindex })
          })
        } else {
          data = {
            reportID: item.id,
            campaignID: item.campaignID,
            lineUpIndex: index
          }
        }
        campaignAPIs.updateLineUpIndex(data)
      })
    },
    updateLegend() {
      let filteredLegend = this.postRightSection.filter(element => {
        let s = this.campaign.reelAnalysisStatus
        if (!s.showEngagement && element.title === 'Engagement Rate by Interactions') return false
        if (!s.showAvgEngagement && element.title === 'Average Engagement Rate') return false
        if (!s.showMediaEngagement && element.title === 'Median Engagement Rate') return false
        if (!s.showAvgLikes && element.title === 'Average Likes') return false
        if (!s.showAvgComments && element.title === 'Average Comments') return false
        if (!s.showAvgViews && element.title === 'Average Views') return false
        if (!s.showAvgReelsPlays && element.title === 'Average Reels Plays') return false
        if (!s.likes && element.title === 'Likes') return false
        if (!s.comments && element.title === 'Comments') return false
        if (!s.showBookmarks && element.title === 'Bookmarks') return false
        if (!s.showShares && element.title === 'Shares') return false
        if (!s.showViews && element.title === 'Views') return false
        if (!s.showReach && element.title === 'Reach') return false
        if (!s.showImpressions && element.title === 'Impressions') return false
        if (!s.showPlays && element.title === 'Total Play Time') return false
        if (!s.showWatches && element.title === 'Average Watch Time') return false
        if (!s.showInteractions && element.title === 'Interactions') return false
        if (!s.liveDate && element.title === 'Live Date') return false
        if (!s.url && element.title === 'Post URL') return false
        return true
      });
      this.postLegends = filteredLegend
    },
    async updatePostAnalysisStatus(items) {
      await this.updateColumnsStatus(items, 'reelAnalysisStatus', 'colSettingsDialog');
    },
    showRequiredActions: function() {
      if (!this.$store.state.token || this.$store.state.token === 'null' || !this.$store.state.user) {
        return false
      } else {
        return config.userRoles.CLIENT !== this.currentUserRole;
      }
    },
    openColSettingsModal() {
      this.initializeReelAnalysisTableCols();
      this.colSettingsDialog = true
    },
    initializeReelAnalysisTableCols() {
        this.tableColsItems = campaignHelper.getOrderedColumns(
          this.showHideFields,
          this.campaign.reelAnalysisStatus,
          this.defaultCols
        );
    },
    async updateColumnsStatus(items, campaignDataProp, dialogProp) {
        let newStatusObject = {};
        items.forEach((item) => {
            newStatusObject[item.key] = item.value;
        });
        let data = [
            Object.assign({}, {
                id: this.campaign.id,
                [campaignDataProp]: newStatusObject
            })
        ];
        try {
            await this.$store.dispatch('campaign/updateCampaignByIds', data);
            this.campaign[campaignDataProp] = newStatusObject;
            this[dialogProp] = false;
        } catch (error) {
            console.error('Error updating campaign settings:', error);
        }
    },
    showPlayTime(time) {
      if (time) {
        return campaignHelper.showReelPlayTime(time)
      }
      return ''
    },
    downloadSummary() {
      const element = document.getElementById('reel-analysis-summary');
      html2canvas(element).then(canvas => {
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = 'reel-analysis-summary.png';
        link.click();
      });
    },
    refreshReportInf(id) {
      this.$emit('refreshReportInf', id)
    }
  }
}
</script>



// WEBPACK FOOTER //
// ReelSummary.vue?dff095a0